import React, { useState } from "react";
import { Button } from "@/components/ui/button";
// Relative: resolvable under Jest without a shim.
import CodeField from "../saas/signup/CodeField";
import { tenantPricing } from "./useSaasCodePreview";
import { formatBillingAmount } from "../admin/billing/_shared";
import { formatMoneyCode } from "../lib/formatMoney";
import { PLATFORM_OFFERS as IDS } from "../constants/testIds/platformOffers";

/**
 * The confirmation step of a plan change, where a platform promo code can ride along
 * (contract §5.3: `promo_code` on change-plan, validated against the TARGET plan).
 *
 * A separate step because a promo's preview is priced against one plan: the code can
 * only be checked once the practitioner has said which plan they are moving to. Picking a
 * plan card opens this step; nothing is changed until Confirm.
 *
 * During the trial the promo is not attached yet: it starts with the first paid invoice
 * (§5.0), and the step says so before the practitioner confirms.
 *
 * @param {object} plan        the target plan row (`/saas/plans`)
 * @param {object} tenant      `billing/summary.tenant`
 * @param {boolean} trialing   `billing/summary.trial.state === "trialing"`
 * @param {object|null} customPrice  `billing/summary.custom_price`; a plan change ends it
 *   (contract D4 §10.1), so the step says so before the practitioner confirms
 * @param {boolean} changing   the change-plan call is in flight
 * @param {(promoCode: string) => void} onConfirm
 * @param {() => void} onCancel
 * @param {function} t         translator bound to "admin"
 */
export default function PlanChangeReview({ plan, tenant, trialing, customPrice = null, changing, onConfirm, onCancel, t }) {
  const [promo, setPromo] = useState(null);
  const params = tenantPricing(tenant, plan.plan_id);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        data-testid={IDS.planChangeReview}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="font-display text-2xl mb-2">{t("bill.offers.reviewTitle", { plan: plan.name })}</h2>
        <p className="text-eh-muted text-sm mb-6">
          {formatBillingAmount(plan.prices?.[tenant.currency], tenant.currency)}
          {t("bill.perMonth")}
        </p>

        {customPrice && (
          <p
            className="mb-4 text-sm rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2"
            data-testid="plan-change-ends-custom-price"
          >
            {t("bill.customPrice.planChangeEnds", {
              price: formatMoneyCode(customPrice.price.cents, customPrice.price.currency),
            })}
          </p>
        )}

        <CodeField
          scope="plan-change"
          value={promo}
          params={params}
          allowedKinds={["promo"]}
          refusedKindText={{ giftcard: t("bill.offers.giftcardNotHere") }}
          disabled={changing}
          onApplied={setPromo}
          onRemoved={() => setPromo(null)}
        />
        {promo && trialing && (
          <p className="mt-2 text-xs text-eh-muted" data-testid={IDS.planChangePromoTrialNote}>
            {t("bill.offers.startsAfterTrial")}
          </p>
        )}

        <div className="mt-8 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={changing} data-testid={IDS.planChangeCancel}>
            {t("bill.offers.cancel")}
          </Button>
          <Button
            onClick={() => onConfirm(promo ? promo.code : "")}
            disabled={changing}
            data-testid={IDS.planChangeConfirm}
            className="bg-sol-gold text-sol-midnight hover:bg-sol-gold/90"
          >
            {t("bill.offers.confirmChange")}
          </Button>
        </div>
      </div>
    </div>
  );
}
